import type { SpellIssue } from '@/lib/spell-check/spell-check-state'
import type { SpellText } from '../types/spell-text'
import type { SpellScanner } from './create-spell-scanner'

/** True when the caret sits inside the word or right at either end of it: the word being typed. */
function isUnderCaret(caretOffset: number | null | undefined, start: number, end: number) {
  return caretOffset != null && caretOffset >= start && caretOffset <= end
}

/**
 * Scans `texts` (one entry per text frame, in reading order) and groups the misspelled instances by
 * word. Grouping follows `caseSensitive`: ON keeps "Thier" and "thier" apart, OFF merges them.
 *
 * A word that a linked chain cut across two frames is checked whole (`continuation`, `skipLeading`)
 * and reported once, on the frame that holds its first half.
 *
 * The word under the caret is skipped (it is still being typed). An issue is marked `overflow` when
 * any of its instances is hidden, even in part, by a frame's unlaid-out tail. A word the layout cuts
 * in half counts, because the user cannot see all of it.
 *
 * Each instance records where it is (`shapeId`, `start`, `end`) and whether it is in overflow,
 * which is what underlines, the overflow peek, picking a word on the canvas and ignoring one
 * occurrence need. Issues carry no suggestions: ask for them
 * with `createSuggester` when a word is opened.
 */
export function buildSpellIssues(
  texts: SpellText[],
  scanner: SpellScanner,
  caseSensitive: boolean
): SpellIssue[] {
  const issues = new Map<string, SpellIssue>()

  for (const {
    text,
    shapeId,
    caretOffset,
    overflowFrom,
    onPath = false,
    continuation = '',
    continuationShapeId,
    skipLeading = 0,
  } of texts) {
    for (const found of scanner.scan(text + continuation)) {
      const { word, start } = found

      // The previous frame already judged the word this piece finishes.
      if (start < skipLeading) continue

      // A word the flow cut in half is reported on the frame where it starts, up to the cut.
      const end = Math.min(found.end, text.length)

      if (isUnderCaret(caretOffset, start, end)) continue

      const id = caseSensitive ? word : word.toLowerCase()
      const inOverflow = overflowFrom != null && end > overflowFrom
      const occurrence = {
        status: 'open' as const,
        shapeId,
        start,
        end,
        // Where the rest of a cut word is, so a Change can rewrite it whole.
        ...(found.end > end &&
          continuationShapeId !== undefined && {
            continuation: { shapeId: continuationShapeId, length: found.end - end },
          }),
        ...(onPath && { onPath: true as const }),
        ...(inOverflow && { overflow: true as const }),
      }
      const existing = issues.get(id)

      if (existing) {
        existing.occurrences.push(occurrence)
        existing.overflow ||= inOverflow
        existing.onPath ||= onPath
        continue
      }

      issues.set(id, {
        id,
        word,
        suggestions: [],
        occurrences: [occurrence],
        overflow: inOverflow,
        onPath,
      })
    }
  }

  return [...issues.values()]
}
